import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

const FALLBACK_LANGUAGE = "pt";
const SUPPORTED_LANGUAGES = ["pt", "en", "es"];

const languageLoaders = {
  pt: () => import("./languages/pt"),
  en: () => import("./languages/en"),
  es: () => import("./languages/es"),
};

const normalizeLanguage = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return FALLBACK_LANGUAGE;

  const [base] = raw.split("-");
  if (SUPPORTED_LANGUAGES.includes(base)) {
    return base;
  }

  return FALLBACK_LANGUAGE;
};

const extractTranslations = (messagesModule, language) => {
  const scoped = messagesModule?.messages?.[language];
  if (!scoped || typeof scoped !== "object") {
    return {};
  }
  return scoped.translations || {};
};

const hasTranslations = (language) =>
  i18n.hasResourceBundle(language, "translations");

const loadLanguageTranslations = async (language) => {
  const scopedLanguage = normalizeLanguage(language);

  const loader = languageLoaders[scopedLanguage] || languageLoaders[FALLBACK_LANGUAGE];
  const module = await loader();
  return {
    scopedLanguage,
    translations: extractTranslations(module, scopedLanguage),
  };
};

const loadLanguage = async (language) => {
  const scopedLanguage = normalizeLanguage(language);

  if (hasTranslations(scopedLanguage)) {
    return scopedLanguage;
  }

  const { translations } = await loadLanguageTranslations(scopedLanguage);
  i18n.addResourceBundle(scopedLanguage, "translations", translations, true, true);

  return scopedLanguage;
};

const resolveInitialLanguage = () => {
  try {
    const search = new URLSearchParams(window.location.search);
    const fromQuery = search.get("lng");
    if (fromQuery) {
      return normalizeLanguage(fromQuery);
    }
  } catch {
    // ignore invalid URL/search access
  }

  try {
    const stored = localStorage.getItem("i18nextLng");
    if (stored) {
      return normalizeLanguage(stored);
    }
  } catch {
    // ignore storage access failures
  }

  if (typeof navigator !== "undefined" && navigator.language) {
    return normalizeLanguage(navigator.language);
  }

  return FALLBACK_LANGUAGE;
};

let bootstrapPromise = null;

const initializeI18n = async () => {
  if (i18n.isInitialized) {
    return i18n;
  }

  if (!bootstrapPromise) {
    bootstrapPromise = (async () => {
      const initialLanguage = resolveInitialLanguage();
      const { scopedLanguage, translations } = await loadLanguageTranslations(initialLanguage);

      await i18n
        .use(LanguageDetector)
        .use(initReactI18next)
        .init({
          debug: false,
          defaultNS: "translations",
          ns: ["translations"],
          fallbackLng: FALLBACK_LANGUAGE,
          lng: scopedLanguage,
          supportedLngs: SUPPORTED_LANGUAGES,
          load: "languageOnly",
          resources: {
            [scopedLanguage]: {
              translations,
            },
          },
          interpolation: {
            escapeValue: false,
          },
          detection: {
            order: ["querystring", "localStorage", "navigator"],
            caches: ["localStorage"],
            lookupQuerystring: "lng",
          },
          react: {
            useSuspense: true,
          },
        });

      i18n.on("languageChanged", (nextLanguage) => {
        void loadLanguage(nextLanguage);
      });

      return i18n;
    })();
  }

  return bootstrapPromise;
};

export { i18n, initializeI18n, normalizeLanguage };
